<template>
  <BaseSingleTemplate v-if="serie">
    <template #template-label>
      <Label name="series" />
    </template>
    <template #share-button>
      <ShareButton />
    </template>
    <template #main-image>
      <SeriesCarousel v-if="setCarouselImages.length" :images="setCarouselImages" />
      <picture v-else>
        <img
          :class="isImageLoaded ? 'fade-in' : 'd-none'"
          :src="$router.options.imagePath + serie.imageUrl"
          @load="isImageLoaded = true"
        />
      </picture>
    </template>
    <template #data-info>
      <div class="data-info-container__header">
        <div
          class="data-info-container__header__item data-info-container__header__item--episode-left"
        >
          <div class="data-info-container__header__item__date">
            {{ formatDate(serie.date) }}
          </div>
          <h1 class="data-info-container__heading">
            {{ serie.title }}
          </h1>
          <h4 class="data-info-container__subheading">
            {{ serie.subTitle }}
          </h4>
          <div class="data-info-container__subheading-container">
            <div class="data-info-container__subheading-container__item">
              <div
                v-if="serie.artists.length"
                class="data-info-container__subheading-container__col data-info-container__subheading-container__col--mb-20"
              >
                <span>WITH</span>
                <router-link
                  v-for="serieArtist in serie.artists"
                  :key="serieArtist.id"
                  :to="{ name: 'Artist', params: { id: serieArtist.permlink } }"
                >
                  {{ serieArtist.name }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="data-info-container__content" v-html="serie.textMain" />
    </template>
    <template v-if="episodes.length" #episodes>
      <div class="episodes-container">
        <div class="episodes-container__inner">
          <SponsorEuCard v-if="isSponsored"
                         :show-name="serie.title"
                         sponsor="sounds-now"/>
          <SponsorEuCard v-if="isSponsoredUlysses"
                         :show-name="serie.title"
                         sponsor="ulysses"/>
          <SponsorEuCard v-if="isSponsoredEu"
                         :show-name="serie.title"
                         sponsor="eu"/>
          <SponsorEuCard v-if="isSponseredTmlab"
                         :show-name="serie.title"
                         sponsor="tmlab"/>
          <SponsorEuCard v-if="isSponsoredAv"
                         :show-name="serie.title"
                         sponsor="av"/>
          <h4 class="episodes-single__heading">
            Episodes
          </h4>
          <div class="episodes-single__grid-mobile">
            <div
              v-for="episode in episodes"
              :key="episode.id"
              class="episodes-single__grid-mobile__item"
            >
              <EpisodeCard :episode="episode"
                           :artist="episode.mainArtist"
                           :artist-name="true"
              />
            </div>
            <div
              v-if="loading"
              class="episodes-single__grid__item episodes-single__grid__item--loader"
            >
              <Loading color="black" />
            </div>
            <div
              v-if="episodes.length && !loading && !pageLimit"
              class="episodes-single__grid episodes-single__grid--observer"
            >
              <IntersectionObserver :init-observer="initObserver" @intersect="loadEpisodes" />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-if="episodes.length" #load-more>
      <div class="load-more">
        <div class="load-more__inner">
          <button
            v-if="!pageLimit"
            :disabled="loading"
            class="load-more__inner__button"
            @click="loadEpisodes"
          >
            <span>VIEW MORE EPISODES</span>
            <span>
              <img
                class="load-more__inner__icon"
                src="@/assets/icons/svg/arrow-down-icon-white.svg"
              />
            </span>
          </button>
          <span v-else>NO MORE EPISODES TO LOAD</span>
        </div>
      </div>
    </template>
  </BaseSingleTemplate>
</template>
<script>
import axios from 'axios';
import BaseSingleTemplate from '@/components/BaseSingleTemplate.vue';
import Label from '@/components/Label.vue';
import ShareButton from '@/components/ui/ShareButton.vue';
import EpisodeCard from '@/components/cards/EpisodeCard.vue';
import Loading from '@/components/ui/Loading.vue';
import IntersectionObserver from '@/components/ui/IntersectionObserver.vue';
import SeriesCarousel from '@/components/sections/SeriesCarousel.vue';
import SponsorEuCard from "@/components/cards/SponsorEuCard.vue";
export default {
  name: 'SerieSingle',
  components: {
      SponsorEuCard,
    Label,
    ShareButton,
    EpisodeCard,
    Loading,
    IntersectionObserver,
    SeriesCarousel,
    BaseSingleTemplate
  },
  data: () => ({
    serie: null,
    setCarouselImages: [],
    episodes: [],
    size: 16,
    page: 0,
    totalPages: null,
    loading: false,
    initObserver: false,
    artist: {},
    BaseSingleTemplate: BaseSingleTemplate,
    isImageLoaded: false,
    isSponsored: false,
    isSponsoredUlysses: false,
    isSponsoredEu: false,
    isSponseredTmlab: false,
    isSponsoredAv: false
  }),
  computed: {
    pageLimit() {
      return this.page === this.totalPages - 1;
    }
  },
  mounted() {
    this.checkObserverStatus();
    this.getSerieSingle();
    this.getSerieEpisodes();
  },
  methods: {
    checkObserverStatus() {
      let mql = window.matchMedia('(max-width: 1200px)');
      if (mql.matches) this.initObserver = true;
      return false;
    },
    getSerieSingle() {
      axios
        .get(this.$router.options.api + '/series/perm/' + this.$route.params.id)
        .then(response => {
          this.serie = response.data;
          this.isSponsored = response.data.sponsored;
          this.isSponsoredUlysses = response.data.sponsoredUlysses;
          this.isSponsoredEu = response.data.sponsoredEu;
          this.isSponseredTmlab = response.data.sponsoredTmlab;
          this.isSponsoredAv = response.data.sponsoredAv;
          if (this.serie.seriesPhotos.length) {
            this.constructCarouselImages();
          }
        })
        .catch(error => console.error(error));
    },
    getSerieEpisodes() {
      this.loading = true;
      axios
        .get(
          this.$router.options.api +
            `/series/episodes/${this.$route.params.id}?size=${this.size}&&page=${this.page}`
        )
        .then(response => {
          if (response.data) {
            this.totalPages = response.data.totalPages;
            const apiData = response.data.content;
            this.episodes = [...this.episodes, ...apiData];
            this.loading = false;
          }
        })
        .catch(e => {
          console.error(e);
          this.loading = false;
        });
    },
    loadEpisodes() {
      this.page++;
      this.scrollToPosition();
      this.getSerieEpisodes();
    },
    scrollToPosition() {
      let mql = window.matchMedia('(max-width: 1200px)');
      if (mql.matches) return;
      setTimeout(() => {
        window.scrollBy({
          top: 900,
          left: 0,
          behavior: 'smooth'
        });
      }, 100);
    },
    constructCarouselImages() {
      const imageModel = [
        {
          id: 99999,
          mediaUrl: this.serie.imageUrl,
          seriesID: this.serie.id,
          sortInt: 0,
          caption: null
        }
      ];
      this.setCarouselImages = [...imageModel, ...this.serie.seriesPhotos];
    },
    formatDate(date) {
      if (date === null) return;
      return date
          .split('.')
          .reverse()
          .join('.');
    },
  }
};
</script>
